import { submitOtherVehicleBasicApplication } from 'app/actions/form/actions';
import { ajaxValidateBorrowerResidence, ajaxValidateCoBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';
import { FieldNames } from 'app/models/fields/names';
import { ovBasicLoanQuestionById, ovBasicLoanQuestionsByName } from 'app/models/questions/ov-loan-basic';
import { Section } from 'app/models/types';
import {
  coBorrowerHasDifferentAddress,
  hasCoBorrower,
  isCoBorrowerEmploymentOther,
  isCoBorrowerRetired,
  isCoEmployee,
  isCoSelfEmployed,
  isEmployee,
  isEmploymentOther,
  isHomeowner,
  isRentFree,
  isRenting,
  isRetired,
  isSelfEmployed,
} from '../fields/conditionals';
import {
  OV_BASIC_LOAN_APP_BORROWER_INCOME_EMPLOYED,
  OV_BASIC_LOAN_APP_BORROWER_INCOME_OTHER,
  OV_BASIC_LOAN_APP_BORROWER_INCOME_RETIRED,
  OV_BASIC_LOAN_APP_BORROWER_INCOME_SOURCE,
  OV_BASIC_LOAN_APP_CITIZENSHIP,
  OV_BASIC_LOAN_APP_COBORROWER_HOUSING_INFORMATION,
  OV_BASIC_LOAN_APP_COBORROWER_INCOME_EMPLOYED,
  OV_BASIC_LOAN_APP_COBORROWER_INCOME_OTHER,
  OV_BASIC_LOAN_APP_COBORROWER_INCOME_RETIRED,
  OV_BASIC_LOAN_APP_COBORROWER_INCOME_SOURCE,
  OV_BASIC_LOAN_APP_COBORROWER_PERSONAL_INFORMATION,
  OV_BASIC_LOAN_APP_CURRENT_HOUSING_SITUATION,
  OV_BASIC_LOAN_APP_FINAL_SECTION,
  OV_BASIC_LOAN_APP_HOUSING_FREE_INFORMATION,
  OV_BASIC_LOAN_APP_HOUSING_OWNER_INFORMATION,
  OV_BASIC_LOAN_APP_HOUSING_RENTER_INFORMATION,
  OV_BASIC_LOAN_APP_PERSONAL_INFORMATION,
  OV_BASIC_LOAN_FINAL_PAGE_QUESTION,
  OV_BASIC_LOAN_GET_LOAN_INFORMATION,
  OV_BASIC_LOAN_GET_VEHICLE_TYPE,
} from './constants';

export const getOVBasicLoanSectionById = (id: number): Section => {
  return OV_BASIC_LOAN_SECTIONS.find((section) => section.id === id) as Section;
};

const OV_BASIC_LOAN_SECTIONS: Section[] = [
  {
    id              : OV_BASIC_LOAN_GET_VEHICLE_TYPE,
    questions       : ovBasicLoanQuestionsByName([
      FieldNames.vehicleType,
      FieldNames.vehicle,
    ]),
    hideBackButton  : true,
    hideProgressBar : true,
  },
  {
    id       : OV_BASIC_LOAN_GET_LOAN_INFORMATION,
    questions: ovBasicLoanQuestionsByName([
      FieldNames.purchasePrice,
      FieldNames.tradeInValue,
      FieldNames.downDollar,
      FieldNames.loanTermDesired,
    ]),
  },
  {
    id       : OV_BASIC_LOAN_APP_PERSONAL_INFORMATION,
    title    : 'Enter your personal information',
    questions: ovBasicLoanQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_COBORROWER_PERSONAL_INFORMATION,
    questions : ovBasicLoanQuestionsByName([
      FieldNames.coBorrowerYN,
      FieldNames.coBorrowerFirstName,
      FieldNames.coBorrowerMiddleName,
      FieldNames.coBorrowerLastName,
      FieldNames.coBorrowerSuffixName,
      FieldNames.coBorrowerEmail,
      FieldNames.coBorrowerDOB,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_CURRENT_HOUSING_SITUATION,
    title     : 'What is your current housing situation?',
    questions : ovBasicLoanQuestionsByName([FieldNames.livingRentOrOwn]),
  },
  {
    id        : OV_BASIC_LOAN_APP_HOUSING_OWNER_INFORMATION,
    showIf    : [isHomeowner],
    title     : 'Provide your homeowner information',
    questions : ovBasicLoanQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id         : OV_BASIC_LOAN_APP_HOUSING_RENTER_INFORMATION,
    showIf     : [isRenting],
    title      : 'Provide your renter information',
    questions  : ovBasicLoanQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id         : OV_BASIC_LOAN_APP_HOUSING_FREE_INFORMATION,
    showIf     : [isRentFree],
    title      : 'Provide your housing information',
    questions  : ovBasicLoanQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id        : OV_BASIC_LOAN_APP_COBORROWER_HOUSING_INFORMATION,
    showIf    : [hasCoBorrower],
    title     : 'Co-borrower at same address as borrower?',
    questions : ovBasicLoanQuestionsByName([
      FieldNames.coBorrowerSameAddress,
      FieldNames.coBorrowerLivingPropertyStreet,
      FieldNames.coBorrowerLivingPropertyStreet2,
      FieldNames.coBorrowerLivingPropertyCity,
      FieldNames.coBorrowerLivingPropertyState,
      FieldNames.coBorrowerLivingPropertyZip,
    ]),
    submitAction : (formValues) =>
      coBorrowerHasDifferentAddress(formValues) ? ajaxValidateCoBorrowerResidence(formValues) : ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : OV_BASIC_LOAN_APP_BORROWER_INCOME_SOURCE,
    title     : 'What is your income source?',
    questions : ovBasicLoanQuestionsByName([FieldNames.employmentType]),
  },
  {
    id         : OV_BASIC_LOAN_APP_BORROWER_INCOME_EMPLOYED,
    showIf     : [isEmployee, isSelfEmployed],
    title      : 'Tell us about your income and employment',
    questions  : ovBasicLoanQuestionsByName([
      FieldNames.employCompanyName,
      FieldNames.employTitle,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employPrevName,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_BORROWER_INCOME_RETIRED,
    showIf    : [isRetired],
    questions : ovBasicLoanQuestionsByName([
      FieldNames.retirementIncome,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_BORROWER_INCOME_OTHER,
    showIf    : [isEmploymentOther],
    questions : ovBasicLoanQuestionsByName([
      FieldNames.otherIncome,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_COBORROWER_INCOME_SOURCE,
    showIf    : [hasCoBorrower],
    title     : `What is your co-borrower's income source?`,
    questions : ovBasicLoanQuestionsByName([
      FieldNames.coBorrowerEmploymentType,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_COBORROWER_INCOME_EMPLOYED,
    showIf    : [isCoEmployee, isCoSelfEmployed],
    title     : `Tell us about your co-borrower's income and employment`,
    questions : ovBasicLoanQuestionsByName([
      FieldNames.coBorrowerEmployCompanyName,
      FieldNames.coBorrowerEmployTitle,
      FieldNames.coBorrowerEmployStart,
      FieldNames.coBorrowerEmployBase,
      FieldNames.coBorrowerEmployPrevName,
      FieldNames.coBorrowerEmployPrevStart,
      FieldNames.coBorrowerEmployPrevEnd,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_COBORROWER_INCOME_RETIRED,
    showIf    : [isCoBorrowerRetired],
    questions : ovBasicLoanQuestionsByName([
      FieldNames.coBorrowerRetirementIncome,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_COBORROWER_INCOME_OTHER,
    showIf    : [isCoBorrowerEmploymentOther],
    questions : ovBasicLoanQuestionsByName([
      FieldNames.coBorrowerOtherIncome,
    ]),
  },
  {
    id        : OV_BASIC_LOAN_APP_CITIZENSHIP,
    title     : 'Borrower questions',
    questions : ovBasicLoanQuestionsByName([
      `${FieldNames.decBorrower}.${FieldNames.hasCitizenship}`,
      `${FieldNames.decCoBorrower}.${FieldNames.hasCitizenship}`,
    ]),
    submitAction : submitOtherVehicleBasicApplication,
    submitText: 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id                : OV_BASIC_LOAN_APP_FINAL_SECTION,
    questions         : [ovBasicLoanQuestionById(OV_BASIC_LOAN_FINAL_PAGE_QUESTION)],
    hideBackButton    : true,
    hideForwardButton : true,
    hideProgressBar   : true,
  },
];
